import { useTranslation } from 'react-i18next';
import { PreviewProps, BrandingProductType } from 'vopay';
import { BrandingValues } from '../BrandingForm/BrandingSettingsForm';

// Saved branding per product. Tiles render the product's own saved values rather than the values
// being edited, so a colour change on one product never shows up on another's tile.
export type SavedBrandingByProduct = Partial<Record<BrandingProductType, BrandingValues>>;

export type PreviewLabelKey = 'vopayPortal' | 'elinx' | 'elinxBankVerify' | 'emails' | 'applicationForm';

export type PreviewOption = {
  key: string;
  productType: BrandingProductType;
  previewType: PreviewProps['preview'];
  labelKey: PreviewLabelKey;
};

export function usePreviewLabels(): Record<PreviewLabelKey, string> {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding.Preview' });

  return {
    vopayPortal: t('vopayPortal', { defaultValue: 'VoPay Portal' }),
    elinx: t('elinx', { defaultValue: 'eLinx' }),
    elinxBankVerify: t('elinxBankVerify', { defaultValue: 'eLinx / Bank Verify' }),
    emails: t('emails', { defaultValue: 'Emails' }),
    applicationForm: t('applicationForm', { defaultValue: 'Application Form' }),
  };
}

const PRODUCT_OPTIONS: Partial<Record<BrandingProductType, PreviewOption[]>> = {
  [BrandingProductType.Portal]: [
    {
      key: 'portal',
      productType: BrandingProductType.Portal,
      previewType: 'portal',
      labelKey: 'vopayPortal',
    },
  ],
  [BrandingProductType.Elinx]: [
    {
      key: 'elinx',
      productType: BrandingProductType.Elinx,
      previewType: 'elinx',
      labelKey: 'elinxBankVerify',
    },
  ],
  [BrandingProductType.Email]: [
    {
      key: 'email',
      productType: BrandingProductType.Email,
      previewType: 'email',
      labelKey: 'emails',
    },
  ],
  [BrandingProductType.Onboarding]: [
    {
      key: 'onboarding',
      productType: BrandingProductType.Onboarding,
      previewType: 'onboarding',
      labelKey: 'applicationForm',
    },
  ],
};

export default function buildPreviewOptions(productTypes: BrandingProductType[]): PreviewOption[] {
  return productTypes.flatMap((productType) => PRODUCT_OPTIONS[productType] ?? []);
}
